import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Tooltip,
} from '@chakra-ui/react';
import { SystemTranslationKeys } from '@common/entities';
import { setLocale } from 'state/slices/localeSlice';
import { RootState } from 'state/store';

import { LanguageIcon } from 'components/custom-icons';
import { useSystemTranslation } from 'hooks';
import { usePropertyLocale } from 'hooks/usePropertyLocale';
import { setFormattingLocale } from 'utils/formatting/formattingLocale';
import { readStoredLaunchQueryParam } from 'utils/viewerLaunchContextSession';

export interface LanguageSwitcherProps {
  /**
   * For screens without `/:orgId/:propertyId` in the URL (e.g. smart route preview in an iframe).
   * When both are set and valid, they are used if URL params are absent.
   */
  orgIdOverride?: number;
  propertyIdOverride?: number;
}

function parsePositiveInt(value: string | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Language switcher dropdown for pre-application screens (login, signup, entry).
 *
 * Responsibilities:
 *   1. Fetch available languages for the current property (once per property).
 *   2. When languages load, apply the property's default only if the current
 *      locale is missing or not supported by that property. URL seeding
 *      (?locale=) is handled by AppWrapper before this runs.
 *   3. Dispatch setLocale on user selection. Values come from the API's
 *      availableLanguages list, so they are already valid locale codes.
 *
 * Resolves org/property from (in order): URL params, optional overrides props,
 * then preview application state (Redux applicant when `isPreviewMode`).
 *
 * URL reading/writing is intentionally absent — that is AppWrapper's concern.
 * Renders nothing when fewer than 2 languages are configured. Languages remain
 * selectable even when translated copy has not yet been configured, so users
 * can still choose their locale for date and number formatting.
 */
export default function LanguageSwitcher({
  orgIdOverride,
  propertyIdOverride,
}: LanguageSwitcherProps) {
  const { tSystem } = useSystemTranslation();
  const { orgId: orgIdParam, propertyId: propertyIdParam } = useParams<{
    orgId: string;
    propertyId: string;
  }>();
  const dispatch = useDispatch();
  const isPreviewMode = useSelector((state: RootState) => state.auth?.isPreviewMode ?? false);
  const applicant = useSelector((state: RootState) => state.application?.applicant);

  const { localeCode, availableLanguages } = useSelector((state: RootState) => state.locale);

  const { effectiveOrgId, effectivePropertyId } = useMemo(() => {
    const urlOrg = parsePositiveInt(orgIdParam);
    const urlProp = parsePositiveInt(propertyIdParam);
    if (urlOrg != null && urlProp != null) {
      return { effectiveOrgId: urlOrg, effectivePropertyId: urlProp };
    }

    const overrideOrg = orgIdOverride != null && orgIdOverride > 0 ? orgIdOverride : null;
    const overrideProp =
      propertyIdOverride != null && propertyIdOverride > 0 ? propertyIdOverride : null;
    if (overrideOrg != null && overrideProp != null) {
      return { effectiveOrgId: overrideOrg, effectivePropertyId: overrideProp };
    }

    const storedOrg = parsePositiveInt(readStoredLaunchQueryParam('orgId'));
    const storedProperty = parsePositiveInt(readStoredLaunchQueryParam('propertyId'));
    if (storedOrg != null && storedProperty != null) {
      return { effectiveOrgId: storedOrg, effectivePropertyId: storedProperty };
    }

    if (
      isPreviewMode &&
      applicant?.org?.id &&
      applicant.propertyId != null &&
      Number(applicant.propertyId) > 0
    ) {
      return {
        effectiveOrgId: applicant.org.id,
        effectivePropertyId: Number(applicant.propertyId),
      };
    }

    return { effectiveOrgId: null as number | null, effectivePropertyId: null as number | null };
  }, [
    orgIdParam,
    propertyIdParam,
    orgIdOverride,
    propertyIdOverride,
    isPreviewMode,
    applicant?.org?.id,
    applicant?.propertyId,
  ]);

  usePropertyLocale({ orgId: effectiveOrgId, propertyId: effectivePropertyId });

  if (availableLanguages.length < 2) return null;

  const currentLanguage =
    availableLanguages.find((lang) => lang.localeCode === localeCode) ??
    availableLanguages.find((lang) => lang.isDefault) ??
    availableLanguages[0];

  const handleLanguageChange = (nextLocale: string) => {
    if (typeof i18nUtils?.setLocale === 'function') {
      i18nUtils.setLocale(nextLocale);
    }
    setFormattingLocale(nextLocale);
    dispatch(setLocale(nextLocale));
  };

  return (
    <>
      <Menu isLazy>
        <Tooltip
          label={tSystem(SystemTranslationKeys.common.selectLanguage)}
          placement="top"
          hasArrow
        >
          <MenuButton
            as={IconButton}
            display={{ base: 'inline-flex', md: 'none' }}
            aria-label={
              currentLanguage
                ? tSystem(SystemTranslationKeys.common.selectLanguageCurrent, {
                    language: currentLanguage.localeName,
                  })
                : tSystem(SystemTranslationKeys.common.selectLanguage)
            }
            icon={<LanguageIcon boxSize={5} />}
            size="sm"
            variant="outline"
            bg="white"
            color="gray.700"
            borderColor="gray.300"
            _hover={{ bg: 'gray.50' }}
            _active={{ bg: 'gray.100' }}
          />
        </Tooltip>
        <MenuList minW="220px">
          {availableLanguages.map((lang) => {
            const isActive = lang.localeCode === currentLanguage?.localeCode;

            return (
              <MenuItem
                key={lang.localeCode}
                onClick={() => handleLanguageChange(lang.localeCode)}
                bg={isActive ? 'gray.50' : 'transparent'}
              >
                <HStack w="100%" justifyContent="space-between">
                  <Box>{lang.localeName}</Box>
                  {isActive && <CheckIcon color="green.500" boxSize={3} />}
                </HStack>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
      <LanguageIcon
        boxSize={4}
        display={{ base: 'none', md: 'block' }}
        color="gray.600"
        aria-hidden="true"
      />
      <Select
        display={{ base: 'none', md: 'block' }}
        value={currentLanguage?.localeCode ?? ''}
        onChange={(e) => handleLanguageChange(e.target.value)}
        size="sm"
        w="auto"
        minW="180px"
        bg="white"
        color="gray.800"
        borderColor="gray.300"
        aria-label={tSystem(SystemTranslationKeys.common.selectLanguage)}
      >
        {availableLanguages.map((lang) => (
          <option key={lang.localeCode} value={lang.localeCode}>
            {lang.localeName}
          </option>
        ))}
      </Select>
    </>
  );
}
